/**
 * Push notification deeplink keys — keep in sync with
 * learn-hmong/constants/deeplinks.ts (`DEEPLINK_PATHS`).
 */

export type PushDeeplinkKey = keyof typeof PUSH_DEEPLINK_PATHS;

/** Short keys → expo-router paths (mirrors mobile `DEEPLINK_PATHS`). */
export const PUSH_DEEPLINK_PATHS = {
  alphabet: '/(tabs)/alphabet',
  resources: '/(tabs)/resources',
  search: '/(tabs)/search',
  settings: '/(tabs)/settings',
  support: '/support',

  modal: '/modal',

  flashcards_numbers: '/(flashcards)/numbers-flashcards',
  flashcards_colors: '/(flashcards)/colors-flashcards',
  flashcards_animals: '/(flashcards)/animals-flashcards',
  flashcards_family: '/(flashcards)/family-flashcards',
  flashcards_food: '/(flashcards)/food-flashcards',
  flashcards_verbs: '/(flashcards)/verbs-flashcards',
  flashcards_favorite: '/(flashcards)/favorite-flashcards',

  game_match_numbers: '/(games)/game-match-numbers',
  game_match_family: '/(games)/game-match-family',
  game_spell_numbers: '/(games)/game-spell-numbers',
  game_animal_guesser: '/(games)/game-animal-guesser',
  game_spell_family: '/(games)/game-spell-family',
  game_match_body: '/(games)/match-the-body-parts',
  game_hmong_village: '/(games)/game-hmong-village',

  diagram_body: '/(diagrams)/body-parts',
  diagram_face: '/(diagrams)/facial-features',

  storybook: '/(storybooks)/storybook',
  storybook_native: '/(storybooks)/storybook-native',
  song: '/(songs)/song',

  cheatsheet_tones_intro: '/(cheatsheets)/intro-to-hmong-tones',
  cheatsheet_useful_terms: '/(cheatsheets)/useful-terms',
  cheatsheet_classifiers: '/(cheatsheets)/hmong-classifiers',
  cheatsheet_negation: '/(cheatsheets)/negation',

  stickers: '/(stickers)/sticker-packs',

  crosswords: '/(crosswords)/daily',
  crosswords_leaderboard: '/(crosswords)/leaderboard',
} as const satisfies Record<string, string>;

export type PushDeeplinkGroup = {
  label: string;
  options: { key: PushDeeplinkKey; label: string }[];
};

/** Grouped options for admin push deeplink dropdowns. */
export const PUSH_DEEPLINK_GROUPS: PushDeeplinkGroup[] = [
  {
    label: 'Tabs',
    options: [
      { key: 'alphabet', label: 'Alphabet' },
      { key: 'resources', label: 'Resources' },
      { key: 'search', label: 'Search' },
      { key: 'settings', label: 'Settings' },
      { key: 'support', label: 'Support chat' },
    ],
  },
  {
    label: 'Flashcards',
    options: [
      { key: 'flashcards_numbers', label: 'Numbers' },
      { key: 'flashcards_colors', label: 'Colors' },
      { key: 'flashcards_animals', label: 'Animals' },
      { key: 'flashcards_family', label: 'Family' },
      { key: 'flashcards_food', label: 'Food' },
      { key: 'flashcards_verbs', label: 'Verbs' },
      { key: 'flashcards_favorite', label: 'Favorites' },
    ],
  },
  {
    label: 'Games',
    options: [
      { key: 'game_match_numbers', label: 'Match numbers' },
      { key: 'game_match_family', label: 'Match family' },
      { key: 'game_spell_numbers', label: 'Spell numbers' },
      { key: 'game_animal_guesser', label: 'Animal guesser' },
      { key: 'game_spell_family', label: 'Spell family' },
      { key: 'game_match_body', label: 'Match body parts' },
      { key: 'game_hmong_village', label: 'Hmong Village' },
    ],
  },
  {
    label: 'Diagrams',
    options: [
      { key: 'diagram_body', label: 'Body parts' },
      { key: 'diagram_face', label: 'Facial features' },
    ],
  },
  {
    label: 'Storybooks & songs',
    options: [
      { key: 'storybook', label: 'Storybook' },
      { key: 'storybook_native', label: 'Native storybook' },
      { key: 'song', label: 'Song' },
    ],
  },
  {
    label: 'Cheatsheets',
    options: [
      { key: 'cheatsheet_tones_intro', label: 'Intro to Hmong tones' },
      { key: 'cheatsheet_useful_terms', label: 'Useful terms' },
      { key: 'cheatsheet_classifiers', label: 'Hmong classifiers' },
      { key: 'cheatsheet_negation', label: 'Negation' },
    ],
  },
  {
    label: 'Stickers',
    options: [{ key: 'stickers', label: 'Sticker packs' }],
  },
  {
    label: 'Crosswords',
    options: [
      { key: 'crosswords', label: 'Daily crosswords (not rolled out yet)' },
      { key: 'crosswords_leaderboard', label: 'Leaderboard (not rolled out yet)' },
    ],
  },
  {
    label: 'Other',
    options: [{ key: 'modal', label: 'Modal' }],
  },
];

export function pushDataFromDeeplink(deeplink: string): Record<string, string> | null {
  if (!deeplink) return null;
  return { deeplink };
}

export function pushDeeplinkPath(deeplink: string): string | null {
  if (!deeplink) return null;
  return PUSH_DEEPLINK_PATHS[deeplink as PushDeeplinkKey] ?? null;
}
